import { apiClient } from './apiClient';
import type { ProjectDetails, ProjectTeamMember } from '@/types/project';

export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ProjectListItem {
  id: string;
  code: string;
  name: string;
  clientId: string;
  clientName: string;
  clientCode?: string;
  billable: boolean;
  active: boolean;
  status: string;
  billingType: string;
  startDate?: string;
  endDate?: string;
  priority: string;
  projectManager?: string;
  teamSize?: number;
  sort?: string;
}

export interface PaginatedProjectsResponse {
  data: ProjectListItem[];
  pagination: PaginationInfo;
}

export interface GetProjectsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  priority?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ProjectStatsResponse {
  totalProjects: number;
  activeProjects: number;
  atRiskCount: number;
  highPriorityCount: number;
  totalTeamMembers: number;
}

/**
 * Convert API response dates (epoch milliseconds) to Date objects
 */
function mapProjectDates(project: any): ProjectDetails {
  return {
    ...project,
    timeline: {
      ...project.timeline,
      startDate: new Date(project.timeline.startDate),
      endDate: new Date(project.timeline.endDate),
      milestones: project.timeline?.milestones?.map((m: any) => ({
        ...m,
        dueDate: new Date(m.dueDate),
      })) || [],
    },
    teamMembers: project.teamMembers?.map((member: any) => ({
      ...member,
      startDate: new Date(member.startDate),
      endDate: member.endDate ? new Date(member.endDate) : undefined,
    })) || [],
    createdAt: new Date(project.createdAt || Date.now()),
    updatedAt: new Date(project.updatedAt || Date.now()),
    lastActivityAt: project.lastActivityAt ? new Date(project.lastActivityAt) : undefined,
  };
}

export const projectsApi = {
  /**
   * Fetch projects with pagination support.
   * @param params - Optional pagination and filter parameters
   * @returns Paginated response with projects and pagination metadata
   */
  async getProjects(params?: GetProjectsParams): Promise<PaginatedProjectsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.priority) queryParams.append('priority', params.priority);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = queryString ? `/projects?${queryString}` : '/projects';
    const response = await apiClient.get<{ data: ProjectListItem[]; pagination?: PaginationInfo }>(url);

    const projects = response.data || [];
    const pagination = response.pagination || {
      page: params?.page || 1,
      pageSize: params?.pageSize || projects.length,
      totalItems: projects.length,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    };

    return { data: projects, pagination };
  },

  /**
   * Fetch project statistics (total, active, at-risk, high-priority counts)
   * @returns Project statistics
   */
  async getProjectStats(): Promise<ProjectStatsResponse> {
    return apiClient.get<ProjectStatsResponse>('/projects/stats');
  },

  /**
   * Fetch all projects with rich details from the API
   * @returns Promise<ProjectDetails[]> Array of projects with rich details
   * @throws ApiError if the request fails
   */
  async getAllProjects(): Promise<ProjectDetails[]> {
    try {
      const projects = await apiClient.get<any[]>('/projects/details');
      return projects.map(mapProjectDates);
    } catch (error) {
      console.error('Failed to fetch projects from API:', error);
      throw error;
    }
  },

  /**
   * Fetch a single project by ID with rich details from the API
   * @param id Project ID
   * @returns Promise<ProjectDetails> Project with rich details
   * @throws ApiError if the request fails
   */
  async getProjectById(id: string): Promise<ProjectDetails> {
    try {
      const project = await apiClient.get<any>(`/projects/${id}/details`);
      return mapProjectDates(project);
    } catch (error) {
      console.error(`Failed to fetch project ${id} from API:`, error);
      throw error;
    }
  },

  /**
   * Fetch team members for a specific project from the API
   * @param projectId Project ID
   * @returns Promise<ProjectTeamMember[]> Array of team members
   * @throws ApiError if the request fails
   */
  async getProjectTeamMembers(projectId: string): Promise<ProjectTeamMember[]> {
    try {
      const teamMembers = await apiClient.get<any[]>(`/projects/${projectId}/team`);
      return teamMembers.map((member: any) => ({
        ...member,
        startDate: new Date(member.startDate),
        endDate: member.endDate ? new Date(member.endDate) : undefined,
      }));
    } catch (error) {
      console.error(`Failed to fetch team members for project ${projectId} from API:`, error);
      throw error;
    }
  },
};
